import React, { useState, useEffect, useCallback } from 'react';
import { FFTable } from '../FFTable';
import { FFTableColumn, FFTableFilterValue, Order } from '../FFTable/types';
import { useVobAuthCodes } from './useVobAuthCodes';
import { VobAuthCode } from './types';
import { MDBox } from '../MDBox';
import { MDTypography } from '../MDTypography';

// Define columns for the auth codes table
const columns: FFTableColumn<VobAuthCode>[] = [
  {
    id: 'authorizationCode',
    label: 'Authorization Code',
    minWidth: 150,
    type: 'text',
    sortable: true,
    filterable: true,
  },
  {
    id: 'policyNumber',
    label: 'Policy Number',
    minWidth: 150,
    type: 'text',
    sortable: true,
    filterable: true,
    renderCell: (value) => value || '-',
  },
  {
    id: 'createdAt',
    label: 'Created Date',
    minWidth: 150,
    type: 'date',
    sortable: true,
    dateFormat: (date: Date) => new Date(date).toLocaleDateString(),
    renderCell: (value) => new Date(value).toLocaleDateString(),
  },
  {
    id: 'updatedAt',
    label: 'Last Updated',
    minWidth: 150,
    type: 'date',
    sortable: false,
    dateFormat: (date: Date) => new Date(date).toLocaleDateString(),
    renderCell: (value) => new Date(value).toLocaleDateString(),
  },
];

export const VobAuthCodeList: React.FC = () => {
  // State for filters
  const [filters, setFilters] = useState<FFTableFilterValue[]>([]);
  const [authCodeFilter, setAuthCodeFilter] = useState<string>('');
  const [policyFilter, setPolicyFilter] = useState<string>('');

  // State for pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // State for sorting
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<Order>('desc');

  // Fetch auth codes using custom hook
  const { authCodes, loading, error, totalCount, totalPages, refetch } =
    useVobAuthCodes({
      page,
      pageSize,
      sortBy,
      sortOrder,
      authCode: authCodeFilter,
      policyNumber: policyFilter,
    });

  // Handle filter change
  const handleFilterChange = useCallback((newFilters: FFTableFilterValue[]) => {
    setFilters(newFilters);

    // Extract auth code and policy filters
    const authCodeFilterItem = newFilters.find(
      (f) => f.columnId === 'authorizationCode'
    );
    const policyFilterItem = newFilters.find(
      (f) => f.columnId === 'policyNumber'
    );

    setAuthCodeFilter(authCodeFilterItem?.value || '');
    setPolicyFilter(policyFilterItem?.value || '');
    setPage(1); // Reset to first page when filters change
  }, []);

  // Handle sort change
  const handleSortChange = useCallback((orderBy: string, order: Order) => {
    setSortBy(orderBy);
    setSortOrder(order);
    setPage(1); // Reset to first page when sort changes
  }, []);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when page size changes
  }, []);

  // Refresh data when filters or pagination changes
  useEffect(() => {
    refetch();
  }, [
    page,
    pageSize,
    sortBy,
    sortOrder,
    authCodeFilter,
    policyFilter,
    refetch,
  ]);

  return (
    <MDBox mt={4}>
      <MDBox mb={2}>
        <MDTypography variant="h5" fontWeight="medium">
          Authorization Codes
        </MDTypography>
        <MDTypography variant="body2" color="text">
          View and manage VOB authorization codes and associated policies
        </MDTypography>
      </MDBox>

      <FFTable
        columns={columns}
        rows={authCodes}
        loading={loading}
        error={error}
        serverSide={true}
        onFilterChange={handleFilterChange}
        initialFilters={filters}
        filterColumnsOptions={['authorizationCode', 'policyNumber']}
        onSortChange={handleSortChange}
        initialSort={{ orderBy: sortBy, order: sortOrder }}
        rowKey="id"
        pagination={{
          page,
          pageSize,
          total: totalCount,
          totalPages,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
          pageSizeOptions: [10, 25, 50, 100],
        }}
        emptyStateMessage="No authorization codes found"
        errorStateMessage="Failed to load authorization codes"
        loadingStateMessage="Loading authorization codes..."
      />
    </MDBox>
  );
};
